/**
 * Icons.
 *
 * Drawn from plain views so the app carries no icon-font or SVG dependency and
 * every glyph inherits a theme colour. Each is a small, deliberate shape — not
 * a general icon system — because the designs use a fixed, small set.
 */

import { memo } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { colors, radius, sizing } from '../../theme';
import { Text } from './Text';

export interface IconProps {
  size?: number;
  color?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/** Three stacked bars — the drawer trigger, left-aligned per the UX rules. */
export const MenuIcon = memo(function MenuIcon({
  size = sizing.icon.lg,
  color = colors.text.primary,
  testID = 'icon-menu',
}: IconProps) {
  const barHeight = Math.max(2, size * 0.1);
  return (
    <View style={{ width: size, height: size, justifyContent: 'center', gap: size * 0.18 }} testID={testID}>
      {[0, 1, 2].map((i) => (
        <View key={i} style={{ height: barHeight, borderRadius: barHeight, backgroundColor: color }} />
      ))}
    </View>
  );
});

/** Left-pointing chevron for drill-down screens. */
export const BackIcon = memo(function BackIcon({
  size = sizing.icon.lg,
  color = colors.text.primary,
  testID = 'icon-back',
}: IconProps) {
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }} testID={testID}>
      <View
        style={{
          width: size * 0.42,
          height: size * 0.42,
          borderLeftWidth: 2,
          borderBottomWidth: 2,
          borderColor: color,
          transform: [{ rotate: '45deg' }],
          marginLeft: size * 0.1,
        }}
      />
    </View>
  );
});

/** Right-pointing chevron used on every navigable list row. */
export const ChevronIcon = memo(function ChevronIcon({
  size = sizing.icon.md,
  color = colors.text.tertiary,
  testID = 'icon-chevron',
}: IconProps) {
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }} testID={testID}>
      <View
        style={{
          width: size * 0.4,
          height: size * 0.4,
          borderRightWidth: 2,
          borderTopWidth: 2,
          borderColor: color,
          transform: [{ rotate: '45deg' }],
          marginRight: size * 0.1,
        }}
      />
    </View>
  );
});

/** Filled circle with a white tick — completed steps and success states. */
export const CheckIcon = memo(function CheckIcon({
  size = sizing.icon.lg,
  color = colors.status.success.solid,
  testID = 'icon-check',
}: IconProps) {
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: color,
        alignItems: 'center',
        justifyContent: 'center',
      }}
      testID={testID}
    >
      <View
        style={{
          width: size * 0.3,
          height: size * 0.52,
          borderRightWidth: Math.max(2, size * 0.09),
          borderBottomWidth: Math.max(2, size * 0.09),
          borderColor: colors.text.onBrand,
          transform: [{ rotate: '45deg' }],
          marginTop: -size * 0.08,
        }}
      />
    </View>
  );
});

/** Padlock for the secure-payment CTAs. */
export const LockIcon = memo(function LockIcon({
  size = sizing.icon.md,
  color = colors.text.onBrand,
  testID = 'icon-lock',
}: IconProps) {
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'flex-end' }} testID={testID}>
      <View
        style={{
          width: size * 0.5,
          height: size * 0.32,
          borderTopLeftRadius: size * 0.25,
          borderTopRightRadius: size * 0.25,
          borderWidth: 1.5,
          borderBottomWidth: 0,
          borderColor: color,
        }}
      />
      <View
        style={{
          width: size * 0.78,
          height: size * 0.5,
          borderRadius: 2,
          backgroundColor: color,
        }}
      />
    </View>
  );
});

/** Right-pointing arrow used on forward CTAs. */
export const ArrowIcon = memo(function ArrowIcon({
  size = sizing.icon.md,
  color = colors.text.onBrand,
  testID = 'icon-arrow',
}: IconProps) {
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }} testID={testID}>
      <View style={{ width: size * 0.7, height: 2, backgroundColor: color }} />
      <View
        style={{
          position: 'absolute',
          right: size * 0.15,
          width: size * 0.32,
          height: size * 0.32,
          borderRightWidth: 2,
          borderTopWidth: 2,
          borderColor: color,
          transform: [{ rotate: '45deg' }],
        }}
      />
    </View>
  );
});

/**
 * Named pictograms for feature rows, drawer entries and notification tiles.
 *
 * A single-glyph placeholder keeps every call site stable; replacing these
 * with the licensed icon set later touches only this map.
 */
const GLYPHS: Record<string, string> = {
  choose: '🍱',
  tiffin: '🥡',
  breakfast: '🍳',
  lunch: '🍛',
  dinner: '🍲',
  delivery: '🛵',
  pickup: '🏪',
  clock: '🕒',
  note: '📝',
  donate: '💳',
  prepare: '🍳',
  distribute: '🤝',
  update: '📬',
  nutritious: '🥗',
  hygienic: '🛡',
  care: '💚',
  home: '🏠',
  impact: '📈',
  donations: '🧾',
  help: '❓',
  settings: '⚙️',
  privacy: '🔒',
  person: '👤',
  email: '✉️',
  phone: '📞',
  mobile: '📱',
  location: '📍',
  calendar: '🗓',
  people: '👥',
  price: '🏷',
  heart: '🤍',
  card: '💳',
  bank: '🏦',
  download: '⤓',
  share: '↗',
  bell: '🔔',
  info: 'ℹ️',
  logout: '⎋',
  /** Meal layout toggle: the arrangement being switched *to*. */
  camera: '📸',
  chat: '💬',
  globe: '🌐',
  sparkle: '✨',
  grid: '▦',
  /** Headcount design switch: the counter it switches to. */
  counter: '±',
  list: '☰',
};

export const Glyph = memo(function Glyph({
  name,
  size = sizing.icon.md,
  testID,
}: {
  name: keyof typeof GLYPHS | string;
  size?: number;
  testID?: string;
}) {
  return (
    <Text
      testID={testID ?? `glyph-${name}`}
      style={{ fontSize: size, lineHeight: size * 1.25 }}
      accessibilityElementsHidden
      importantForAccessibility="no"
    >
      {GLYPHS[name] ?? '•'}
    </Text>
  );
});

/** Circular tinted background behind a glyph — the How It Works step markers. */
export const GlyphCircle = memo(function GlyphCircle({
  name,
  size = 48,
  background = colors.bg.brandSubtle,
  testID,
}: {
  name: string;
  size?: number;
  background?: string;
  testID?: string;
}) {
  return (
    <View
      style={[
        styles.circle,
        { width: size, height: size, borderRadius: size / 2, backgroundColor: background },
      ]}
      testID={testID}
    >
      <Glyph name={name} size={size * 0.44} />
    </View>
  );
});

const styles = StyleSheet.create({
  circle: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.full,
  },
});
